import { ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { Pagination } from 'app/shared/pagination';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { WorkOrderService } from 'app/work-order/work-order.service';
import { DeferredAction } from 'app/shared/deferred-action';
import { ModalEditWorkOrderComponent } from '../modal-edit-work-order/modal-edit-work-order.component';
import { Router } from '@angular/router';

declare const Mangler: any;

@Component({
	selector: 'modal-select-work-order',
	templateUrl: './modal-select-work-order.component.html',
	styles: [`
		.sticky-tabs {
			position: sticky;
			top: -1.5rem;
			z-index: 10;
			background-color: #fff;
		}

		tr .sticky {
			top: 2.9rem;
		}
	`]
})
export class ModalSelectWorkOrderComponent extends UIModalBaseComponent {

	@Input() embed = false;

	@ViewChild(ModalEditWorkOrderComponent) modalEditWorkOrder: ModalEditWorkOrderComponent;

	work_orders;
	teamList = [];
	labourTypeList = [];
	areaList = [];
	filterList = [];
	projectList = [];
	systemList = [];
	userList = [];
	labelList = [];
	pagination = new Pagination();
	mode = 'select';
	projectId = null;
	stageList = [];
	refreshAction = new DeferredAction();
	type;
	workOrderSortList = '!work_order_no';
	customerId = null;
	workOrderSearchList = '';

	constructor(
		public app: AppService,
		private api: ApiService,
		public service: WorkOrderService,
		private cdr: ChangeDetectorRef,
		private router: Router
	) {
		super();
	}

	open(_mdata: any = null) {
		this.work_orders = null;
		this.mode = _mdata.mode || 'select';
		this.type = _mdata.type || null;
		this.stageList = this.service.stageList();
		this.service.stageFilter = 'all';
		this.service.clearFilters();
		this.workOrderSortList = '!work_order_no';
		this.workOrderSearchList = '';
		if (this.mode === 'add') {
			this.stageList = ['all', 'to_be_scheduled', 'scheduled', 'active'];
		}
		this.projectId = _mdata.project_id || null;
		this.customerId = _mdata.customer_id || null;
		this.initialseFilters();
		if (this.projectId) {
			this.service.projectFilter = [this.projectId];
		}
		if (this.customerId) {
			this.service.customerFilter = [this.customerId];
		}
		this.refreshList();
		this.modal.open();
		this.cdr.detectChanges();
	}

	selectItem(item) {
		this.modal.close(item);
	}

	initialseFilters() {
		this.api.task.getFilterData({}, list => {
			this.filterList = list;
			this.labourTypeList = list.labour_types;
			this.areaList = list.areas;
			this.teamList = list.teams;
			this.projectList = list.projects;
			this.systemList = list.category_groups;
			this.userList = list.users;
			this.cdr.detectChanges();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});

		this.api.settings.listLabels({ type: ['work_order'] }, data => {
			this.labelList = data || [];
			this.cdr.detectChanges();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	refreshList(delay = 0) {
		const options = {
			stage: this.resolveStage(),
			customer: this.getFilteredValue(this.service.customerFilter),
			project: this.getFilteredValue(this.service.projectFilter),
			system: this.getFilteredValue(this.service.systemFilter),
			area: this.getFilteredValue(this.service.areaFilter),
			labour_type: this.getFilteredValue(this.service.labourTypeFilter),
			team: this.getFilteredValue(this.service.teamFilter),
			user: this.getFilteredValue(this.service.userFilter),
			labels: this.getFilteredValue(this.service.labelFilter),
			sort: this.workOrderSortList,
			paginated: 1,
			page_size: this.pagination.pageSize,
			page_number: this.pagination.page,
			keywords: this.workOrderSearchList,
			...(this.type && {
				type: this.type === 'service_job' ? ['service'] : ['contract', 'sales']
			})
		};

		const deferredCount = this.refreshAction.run(delay, () => {
			this.refreshAction.wait();
			this.api.task.listWorkOrders(options, data => {
				this.refreshAction.release();
				if (deferredCount === this.refreshAction.count) {
					this.work_orders = data;
					this.pagination.applyData(data.list);
				}
				this.cdr.detectChanges();
			}, error => {
				if (deferredCount === this.refreshAction.count) {
					this.app.notifications.showDanger(error.message);
				}
			});
		});
	}

	resolveStage(s = null) {
		if (this.mode === 'add') {
			if (this.service.resolveStageFilter(s) == null) return ['to_be_scheduled', 'scheduled', 'active'];
			return this.service.resolveStageFilter(s);
		}
		return this.service.resolveStageFilter(s);
	}

	// Helper function to determine filtered value or null
	getFilteredValue(filterArray) {
		return (filterArray.length === 0) ? null : filterArray;
	}

	getLabelData(id) {
		return this.work_orders?.labels.find(item => item.id === id);
	}

	selectStageFilter(stage) {
		if (this.service.stageFilter === stage) return;
		this.service.stageFilter = stage;
		this.refreshList();
	}

	stageTotal(s) {
		if (!this.work_orders) return '';
		let list = this.resolveStage(s);
		if (!list) list = Object.keys(this.work_orders.stage_totals);

		let total = 0;
		list.forEach(stage => {
			total += this.work_orders.stage_totals[stage]?.count || 0;
		});

		return total;
	}

	sortClass(field) {
		if (this.workOrderSortList === field) {
			return 'wq wq-caret-up text-secondary text-large';
		} else if (this.workOrderSortList === '!' + field) {
			return 'wq wq-caret-down text-secondary text-large';
		} else {
			return '';
		}
	}

	sortBy(field) {
		if (this.workOrderSortList === field) {
			this.workOrderSortList = '!' + field;
		} else {
			this.workOrderSortList = field;
		}

		this.refreshList();
	}

	handleSelectionChange(data, filterType: string): void {
		const filterMap = {
			project: 'projectFilter',
			system: 'systemFilter',
			area: 'areaFilter',
			labourType: 'labourTypeFilter',
			team: 'teamFilter',
			user: 'userFilter',
			customer: 'customerFilter',
			label: 'labelFilter'
		};

		const filterProperty = filterMap[filterType];
		if (filterProperty) {
			this.service[filterProperty] = Mangler.clone(data);
			this.refreshList();
		}
	}

	clearAllFilters() {
		if (!this.projectId) this.service.projectFilter = [];
		if (!this.customerId) this.service.customerFilter = [];
		this.service.systemFilter = [];
		this.service.areaFilter = [];
		this.service.labourTypeFilter = [];
		this.service.teamFilter = [];
		this.service.userFilter = [];
		this.service.labelFilter = [];
		this.refreshList();
	}

	isAnyFilterActive(): boolean {
		const filters = [
			this.service.systemFilter,
			this.service.areaFilter,
			this.service.labourTypeFilter,
			this.service.teamFilter,
			this.service.userFilter,
			this.service.labelFilter,
		];

		if (!this.projectId) filters.push(this.service.projectFilter);
		if (!this.customerId) filters.push(this.service.customerFilter);
		return filters.some(filter => filter.length > 0);
	}

	createNewWorkOrder() {
		this.modalEditWorkOrder.open({
			id: 'new',
			project_id: this.projectId
		});
	}

	workOrderEditModalClosed(data) {
		if (data) this.modal.close(data);
	}

	newServiceJob() {
		if (!this.projectId) {
			this.app.notifications.showDanger('Please select a project.');
			return;
		}
		this.api.task.newWorkOrder({ project: this.projectId }, data => {
			data.record.billable = 1;
			this.modal.close(data.record);
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	jobsAvailableInBookingMode() {
		return (this.mode !== 'book' || (this.work_orders && (this.stageTotal('all') || 0) > 0));
	}

	newJob() {
		if (this.type) {
			if (this.type === 'service_job') this.newServiceJob();
			else {
				if (this.projectId) this.router.navigate(['/', this.app.org, 'project', this.projectId, 'task-builder']);
				else this.app.notifications.showDanger('Please select a Project');
			}
		}
	}

}
